import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import AddGuestDialog from '../components/AddGuestDialog';
import { Search, Plus, Upload, Download, CheckCircle2, XCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const GuestsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [events, setEvents] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  const loadGuests = () => {
    setLoading(true);
    api
      .get('/guests', {
        params: {
          event_id: selectedEvent === 'all' ? undefined : selectedEvent,
          search: searchTerm || undefined,
        },
      })
      .then((res) => setGuests(res.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/events').then((res) => setEvents(res.data || []));
  }, []);

  useEffect(() => {
    loadGuests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, searchTerm]);

  const targetEventId = selectedEvent !== 'all' ? selectedEvent : events[0]?.id;

  const handleImportClick = () => {
    if (selectedEvent === 'all') {
      toast({ title: 'Select an event to import guests into', variant: 'destructive' });
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await api.post(`/events/${selectedEvent}/guests/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({
        title: 'Import complete',
        description: `${res.data.imported} imported, ${res.data.failed} failed`,
      });
      loadGuests();
    } catch (error) {
      toast({ title: 'Import failed', description: error?.error?.message, variant: 'destructive' });
    } finally {
      e.target.value = '';
    }
  };

  const filteredGuests = guests;

  if (loading && guests.length === 0) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold">Guest Management</h1>
          <p className="text-gray-600 mt-1">Manage your event guests and invitations</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            accept=".csv,text/csv"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
          />
          <Button variant="outline" onClick={handleImportClick}>
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          <AddGuestDialog
            eventId={targetEventId}
            onGuestAdded={loadGuests}
            trigger={
              <Button className="bg-orange-500 hover:bg-orange-600" disabled={!targetEventId}>
                <Plus className="w-4 h-4 mr-2" />
                Add Guest
              </Button>
            }
          />
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search guests by name, email or company..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={selectedEvent} onValueChange={setSelectedEvent}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Filter by event" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {events.map((event) => (
                <SelectItem key={event.id} value={event.id}>
                  {event.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </Card>

      {/* Guests Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr className="border-b">
                <th className="text-left py-4 px-6 font-semibold">Guest</th>
                <th className="text-left py-4 px-6 font-semibold">Contact</th>
                <th className="text-left py-4 px-6 font-semibold">Event</th>
                <th className="text-left py-4 px-6 font-semibold">QR Code</th>
                <th className="text-left py-4 px-6 font-semibold">Status</th>
                <th className="text-left py-4 px-6 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredGuests.map((guest) => {
                const event = events.find((e) => e.id === guest.eventId);
                return (
                  <tr key={guest.id} className="border-b hover:bg-gray-50 transition">
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-semibold">{guest.name}</p>
                        <p className="text-sm text-gray-600">{guest.company}</p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="text-sm">
                        <p className="text-gray-800">{guest.email}</p>
                        <p className="text-gray-600">{guest.phone}</p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <p className="text-sm font-medium">{event?.name}</p>
                      <p className="text-xs text-gray-600">
                        {event && new Date(event.date).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="py-4 px-6">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {guest.qrCode}
                      </code>
                    </td>
                    <td className="py-4 px-6">
                      {guest.attended ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Attended
                        </Badge>
                      ) : guest.registered ? (
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                          Registered
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">
                          <XCircle className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm">
                          View
                        </Button>
                        <Button variant="ghost" size="sm">
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {filteredGuests.length === 0 && (
        <Card className="p-12 text-center">
          <p className="text-gray-500">No guests found</p>
        </Card>
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-sm text-gray-600">Total Guests</p>
          <p className="text-2xl font-bold mt-1">{filteredGuests.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-600">Registered</p>
          <p className="text-2xl font-bold mt-1">
            {filteredGuests.filter((g) => g.registered).length}
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-gray-600">Attended</p>
          <p className="text-2xl font-bold mt-1">
            {filteredGuests.filter((g) => g.attended).length}
          </p>
        </Card>
      </div>
    </div>
  );
};

export default GuestsPage;